import { gql, useSubscription, useMutation, useQuery } from "@apollo/client";
import { StorageHelper } from "../utils/storageHelper";
import { client } from "../apolloClient";

/**
 * useNotification custom hook for notification operations.
 * - Provides real-time notification subscription, fetching, and marking as read.
 * - Handles cache updates for notifications.
 * - Exposes helpers for reading/writing notifications in Apollo cache.
 * - Returns methods for subscribing, fetching, marking as read, and cache helpers.
 */

// GraphQL Queries, Mutations, and Subscriptions
const SUB_NOTIFICATION = gql`
  subscription Subscription(
    $role_id: String!
    $resource_id: String!
    $hostname: String!
  ) {
    PushNotification(
      role_id: $role_id
      resource_id: $resource_id
      hostname: $hostname
    ) {
      notification_id
      message
      is_read
      created_on
      action_link
      resource_id
    }
  }
`;

const GET_NOTIFICATIONS = gql`
  query GetNotificationsByResourceById($resourceId: ID!) {
    GetNotificationsByResourceById(resourceId: $resourceId) {
      notifications {
        notification_id
        message
        is_read
        created_on
        action_link
        resource_id
      }
    }
  }
`;

const READ_NOTIFICATIONS = gql`
  mutation ReadNotificationByNotificationIds($notificationIds: [ID!]!) {
    ReadNotificationByNotificationIds(notification_ids: $notificationIds) {
      notification_id
      message
      is_read
      created_on
      action_link
      role_id
      resource_id
      hostname
    }
  }
`;

// Cache operations for notifications
const cacheOperations = {
  /**
   * Reads notifications from Apollo cache for a given resourceId.
   * @param {string|number} resourceId
   * @returns {Array} Array of notification objects or empty array if not found.
   */
  readNotifications: (resourceId) => {
    try {
      const { GetNotificationsByResourceById = { notifications: [] } } =
        client.readQuery({
          variables: { resourceId: parseInt(resourceId) },
          query: GET_NOTIFICATIONS,
        }) || {};
      return GetNotificationsByResourceById.notifications;
    } catch {
      return [];
    }
  },
  /**
   * Writes notifications to Apollo cache for a given resourceId.
   * @param {Array} notifications
   * @param {string|number} resourceId
   */
  writeNotifications: (notifications, resourceId) => {
    client.writeQuery({
      variables: { resourceId: parseInt(resourceId) },
      query: GET_NOTIFICATIONS,
      data: { GetNotificationsByResourceById: { notifications } },
    });
  },
};

export const useNotification = () => {
  // Real-time notification subscription
  const useNotify = useSubscription(SUB_NOTIFICATION, {
    variables: {
      role_id: StorageHelper.get("userDetails")?.role_id.toString(),
      resource_id: StorageHelper.get("userDetails")?.resource_id.toString(),
      hostname: StorageHelper.get("schemaInfo")?.hostname.toString(),
    },
  });

  // Query for fetching notifications
  const { data: notificationsData, loading: notificationsLoading, refetch: refetchNotifications } = useQuery(GET_NOTIFICATIONS, {
    variables: {
      resourceId: parseInt(StorageHelper.get("userDetails")?.resource_id.toString()),
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  // Mutation for marking notifications as read
  const [readNotifications] = useMutation(READ_NOTIFICATIONS, {
    update(cache, { data: { ReadNotificationByNotificationIds } }) {
      try {
        const resourceId = parseInt(StorageHelper.get("userDetails")?.resource_id.toString());
        const existingData = cache.readQuery({
          query: GET_NOTIFICATIONS,
          variables: { resourceId },
        });

        if (existingData) {
          const newNotifications = existingData.GetNotificationsByResourceById.notifications.map(notification => {
            if (ReadNotificationByNotificationIds.some(read => read.notification_id === notification.notification_id)) {
              return { ...notification, is_read: true };
            }
            return notification;
          });

          cache.writeQuery({
            query: GET_NOTIFICATIONS,
            variables: { resourceId },
            data: {
              GetNotificationsByResourceById: {
                notifications: newNotifications,
              },
            },
          });
        }
      } catch (error) {
        console.error("Error updating cache after marking notifications as read:", error);
      }
    },
  });

  /**
   * Marks notifications as read by their IDs.
   * @param {Array} notificationIds
   */
  const markAsRead = async (notificationIds) => {
    try {
      await readNotifications({
        variables: {
          notificationIds: notificationIds.map(id => parseInt(id))
        }
      });
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  return { 
    useNotify, 
    notificationsData, 
    notificationsLoading, 
    markAsRead,
    refetchNotifications,
    cacheOperations
  };
};
